import * as d3 from 'd3';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { v4 } from 'uuid';

import { GcpModel, GeodataModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import {
  GeodataMarker,
  GeodataMarkerEstimate,
} from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { t } from '../../../../utils/i18-next';
import { sessionStorageUtils } from '../../../../utils/session-storage-utils';
import { isDefined } from '../../../../utils/variables';
import { useButtonHotkey } from '../../../hooks/use-button-hotkey';
import { useToaster } from '../../../state/toaster-state';
import { Center } from '../../../styles/center';
import { Button } from '../../button/button';
import { MenuItem } from '../../context-menu/components/menu-item';
import { ContextMenu } from '../../context-menu/context-menu';
import { downloadBlob } from '../../skyview-new/components/report-tool-menu/helpers/utils';
import { ToolIcon } from '../../skyview-new/toolbar/tool-icon';
import { ToolbarGroup } from '../../skyview-new/toolbar/toolbar-group';
import { Stack } from '../../stack/stack';
import { GeodataContext } from '../geodata-state';
import { AlignerContext } from './aligner-state';
import { sfmViewerLayering } from './layering';
import { getMarkerGroup, pinnedGreen } from './marker-svg-utils';
import { GeodataImageItemWithMarkers, PlaceMarkersContext } from './place-markers-state';
import { usePatchMarkerEstimatesMutation } from './use-patch-marker-estimates-mutation';

function selectNextOrPreviousImage(
  allImages: GeodataImageItemWithMarkers[],
  sortedImages: { imageId: string; gcpId?: string }[],
  geodata: GeodataModel,
  selectImage: (value: GeodataImageItemWithMarkers) => void,
  selectGcp: (value: GcpModel) => void,
  selectedImage: GeodataImageItemWithMarkers,
  selectedGcp: GcpModel | undefined,
  direction: 'next' | 'previous',
) {
  const index = sortedImages.findIndex(
    (x) =>
      x.imageId === selectedImage.imageId && (!isDefined(x.gcpId) || x.gcpId === selectedGcp?.id),
  );

  if (sortedImages.length > 0) {
    const nextIndex =
      index < 0
        ? 0
        : direction === 'next'
          ? (index + 1) % sortedImages.length
          : (index - 1 + sortedImages.length) % sortedImages.length;

    const nextItem = sortedImages[nextIndex];
    const nextImageId = nextItem?.imageId;
    const nextGcpId = nextItem?.gcpId;
    const nextImage = allImages.find((x) => x.imageId === nextImageId);
    const nextGcp = geodata.gcpCollection?.points.find((x) => x.id === nextGcpId);

    if (isDefined(nextImageId) && isDefined(nextImage)) {
      selectImage(nextImage);
    }
    if (isDefined(nextGcp)) {
      selectGcp(nextGcp);
    }
  }
}

function getDefaultTransformation(
  imgWidth: number,
  imgHeight: number,
  existingMarker: GeodataMarker | undefined,
  zoomRef: ZoomRef | undefined,
  svg: SVGSVGElement | null,
) {
  if (isDefined(svg) && isDefined(zoomRef) && isDefined(existingMarker)) {
    const gcpChanged = existingMarker.gcpId !== zoomRef.gcpId;
    const imageChanged = existingMarker.imageId !== zoomRef.imageId;

    // If neither the GCP or image changed. Then keep the current zoom.
    if (!gcpChanged && !imageChanged) {
      return zoomRef.transform;
    }

    // Center on the original marker position
    const [normalizedX, normalizedY] = existingMarker.position;
    const x = normalizedX * imgWidth;
    const y = normalizedY * imgHeight;

    const scale = zoomRef.transform.k;
    const svgWidth = svg.clientWidth;
    const svgHeight = svg.clientHeight;

    // Compute translation to center the marker.
    const translateX = -x * scale + svgWidth / 2;
    const translateY = -y * scale + svgHeight / 2;

    return d3.zoomIdentity.translate(translateX, translateY).scale(scale);
  }

  // Reset zoom if there is no marker to zoom on.
  return d3.zoomIdentity;
}

function getMouseGroup(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  size: number,
  strokeWidth: number,
  label?: string,
) {
  // Create a group for the mouse circle and add the circle element inside.
  const mouseCircleGroup = svg.append('g').style('display', 'none');

  const radius = size * 1.5;
  mouseCircleGroup
    .append('circle')
    .attr('r', radius)
    .attr('fill', 'none')
    .attr('stroke', 'white')
    .attr('stroke-width', strokeWidth);

  // Four black lines inside the circle (not reaching the center)
  const lineStart = radius * 0.2; // 20% of the circle radius
  const lineEnd = radius * 0.8; // 20% of the circle radius

  // Vertical line (top)
  mouseCircleGroup
    .append('line')
    .attr('x1', 0)
    .attr('y1', -lineStart)
    .attr('x2', 0)
    .attr('y2', -lineEnd)
    .attr('stroke', 'black')
    .attr('stroke-width', strokeWidth / 2);

  // Vertical line (bottom)
  mouseCircleGroup
    .append('line')
    .attr('x1', 0)
    .attr('y1', lineStart)
    .attr('x2', 0)
    .attr('y2', lineEnd)
    .attr('stroke', 'black')
    .attr('stroke-width', strokeWidth / 2);

  // Horizontal line (left).
  mouseCircleGroup
    .append('line')
    .attr('x1', -lineStart)
    .attr('y1', 0)
    .attr('x2', -lineEnd)
    .attr('y2', 0)
    .attr('stroke', 'black')
    .attr('stroke-width', strokeWidth / 2);

  // Horizontal line (right).
  mouseCircleGroup
    .append('line')
    .attr('x1', lineStart)
    .attr('y1', 0)
    .attr('x2', lineEnd)
    .attr('y2', 0)
    .attr('stroke', 'black')
    .attr('stroke-width', strokeWidth / 2);

  // Center yellow dot with a black border.
  mouseCircleGroup
    .append('circle')
    .attr('r', 4)
    .attr('fill', 'yellow')
    .attr('stroke', 'black')
    .attr('stroke-width', 1)
    .attr('cx', 0)
    .attr('cy', 0);

  // Add label to the bottom right of the dot if provided
  if (label) {
    // Append a rect behind the text for background
    const textPadding = 2;
    const text = mouseCircleGroup
      .append('text')
      .attr('x', radius * 1.1)
      .attr('y', 0)
      .attr('fill', 'black')
      .attr('font-size', '1.2em')
      .text(label);

    const bbox = text.node()!.getBBox();
    mouseCircleGroup
      .insert('rect', 'text') // Insert rect before text
      .attr('x', bbox.x - textPadding)
      .attr('y', bbox.y - textPadding)
      .attr('width', bbox.width + 2 * textPadding)
      .attr('height', bbox.height + 2 * textPadding)
      .attr('fill', 'rgba(255, 255, 255, 0.8)') // Transparent white background
      .attr('rx', 3) // Optional rounded corners
      .attr('ry', 3);
  }

  return mouseCircleGroup;
}

type ZoomRef = {
  transform: d3.ZoomTransform;
  gcpId?: string;
  imageId?: string;
};

/**
 * Gets the estimate being used for display, if any.
 * Returns the estimate object if the marker is using an estimate, otherwise null.
 */
function getMarkerEstimate(
  marker: GeodataMarker,
  estimates: GeodataMarkerEstimate[],
): Required<GeodataMarkerEstimate> | undefined {
  // If pinned, it's not using an estimate
  if (marker.pinned) {
    return undefined;
  }

  // If no estimates available, it's not using an estimate
  if (estimates.length === 0) {
    return undefined;
  }

  // Find estimates for this marker
  const markerEstimates = estimates.filter((e) => e.markerId === marker.id);

  if (markerEstimates.length === 0) {
    return undefined;
  }

  // Find ACCEPTED estimates, sort by confidence descending
  const acceptedEstimates = markerEstimates
    .filter((e) => e.status === 'ACCEPTED' && isDefined(e.position))
    .sort((a, b) => b.confidence - a.confidence);

  if (acceptedEstimates.length > 0 && isDefined(acceptedEstimates[0].position)) {
    return { ...acceptedEstimates[0], position: acceptedEstimates[0].position };
  }

  // Find PENDING estimates, sort by confidence descending
  const pendingEstimates = markerEstimates
    .filter((e) => e.status === 'PENDING' && isDefined(e.position))
    .sort((a, b) => b.confidence - a.confidence);

  if (pendingEstimates.length > 0 && isDefined(pendingEstimates[0].position)) {
    return { ...pendingEstimates[0], position: pendingEstimates[0].position };
  }

  return undefined;
}

function getUpdatedReprojectionError(
  newPosition: [number, number],
  marker?: GeodataMarker,
): [number, number] | undefined {
  if (!isDefined(marker) || !isDefined(marker.reprojectionError)) {
    return undefined;
  }

  return [
    marker.reprojectionError[0] + (marker.position[0] - newPosition[0]),
    marker.reprojectionError[1] + (marker.position[1] - newPosition[1]),
  ];
}

export const PlaceMarkerViewer = () => {
  const { signer, geodata } = useContext(GeodataContext);
  const theme = useTheme();
  const { readyToOptimize } = useContext(AlignerContext);
  const { showToast } = useToaster();
  const {
    gcp,
    image,
    selectGcp,
    selectImage,
    imagesWithMarkers,
    showPlaceMarkersDialog,
    setShowPlaceMarkersDialog,
    updateMarker,
    deleteMarker,
    sortedImageList,
    estimates,
  } = useContext(PlaceMarkersContext);

  const svgRef = useRef<SVGSVGElement>(null);
  const closeButtonRef = useRef(null);
  const previousButtonRef = useRef(null);
  const nextButtonRef = useRef(null);
  const zoomTransformRef = useRef<ZoomRef>({ transform: d3.zoomIdentity });

  // Zoom persistence per image so rebuilds (marker/theme/estimates updates) don't reset zoom
  const zoomByImageRef = useRef(new Map<string, d3.ZoomTransform>());
  const prevImageKeyRef = useRef<string | null>(null);
  const imageKeyRef = useRef<string>('none');

  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const lastMouseDownRef = useRef<number>(0);
  const lastMousePositionRef = useRef<{ clientX: number; clientY: number } | undefined>(undefined);
  const sceneRef = useRef<{
    imageLayer?: d3.Selection<SVGGElement, unknown, null, undefined>;
    markersLayers: {
      factorX: number;
      factorY: number;
      element: d3.Selection<SVGGElement, unknown, null, undefined>;
    }[];
    mouseCircleGroup?: d3.Selection<SVGGElement, unknown, null, undefined>;
    imgWidth?: number;
    imgHeight?: number;
    mouseFactorX: number;
    mouseFactorY: number;
    existingGcpId?: string;
    existingImageId?: string;
  }>({ markersLayers: [], mouseFactorX: 0, mouseFactorY: 0 });

  const [markerContextMenu, setMarkerContextMenu] = useState<{
    x: number;
    y: number;
    marker: GeodataMarker | undefined;
    activeEstimate: GeodataMarkerEstimate | undefined;
  } | null>(null);

  const { patchMarkerEstimatesAsync, isPending: isPatchingEstimate } =
    usePatchMarkerEstimatesMutation(geodata.id);

  const [patchImageLoading, setPatchImageLoading] = useState(false);
  const [fullResolution, setFullResolution] = useState(() => {
    const stored = sessionStorageUtils.getUserItemOrDefault('placeMarkersFullResolution', 'false');
    return stored === 'true';
  });
  const [downloadLoading, setDownloadLoading] = useState(false);

  useButtonHotkey(closeButtonRef, 'Escape');
  useButtonHotkey(previousButtonRef, 'ArrowUp');
  useButtonHotkey(previousButtonRef, 'PageUp');
  useButtonHotkey(nextButtonRef, 'ArrowDown');
  useButtonHotkey(nextButtonRef, 'PageDown');

  // Stable key for "same image" to avoid effect re-runs when only object reference changes
  const imageKey = useMemo(() => image?.imageIdentifier ?? image?.imageId ?? 'none', [image]);
  const imageIdentifier = useMemo(() => image?.imageIdentifier, [image]);
  const imageFile = useMemo(() => image?.file, [image]);

  const imageSrc = useMemo(() => {
    if (!signer || !imageIdentifier) {
      return null;
    }

    if (imageFile) {
      return `file:${imageFile.name}:${imageFile.size}:${imageFile.lastModified}`;
    }

    const pathPrefix = fullResolution ? 'photos' : 'previews';
    return signer.getSignedRequest(`${pathPrefix}/${imageIdentifier}`);
  }, [signer, imageIdentifier, imageFile, fullResolution]);

  const handleDownloadImage = useCallback(async () => {
    if (!isDefined(signer) || !isDefined(image) || !isDefined(image.imageIdentifier)) {
      return;
    }

    try {
      setDownloadLoading(true);
      const fullResolutionUrl = signer.getSignedRequest(`photos/${image.imageIdentifier}`);
      const response = await fetch(fullResolutionUrl);
      const blob = await response.blob();

      // Determine file extension from imageIdentifier or default to jpg
      const fileExtension = image.imageIdentifier.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = image.displayName || image.imageIdentifier.split('.')[0] || 'image';

      downloadBlob(blob, fileName, fileExtension);
    } catch (error) {
      console.error('Failed to download image:', error);
    } finally {
      setDownloadLoading(false);
    }
  }, [signer, image]);

  // Create zoom behavior once; handler reads from sceneRef so it doesn't need redefinition
  useEffect(() => {
    if (!svgRef.current) {
      return;
    }
    if (zoomRef.current) {
      return;
    }

    zoomRef.current = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 1000])
      .wheelDelta(
        (event) =>
          -event.deltaY *
          (event.deltaMode === 1 ? 0.05 : event.deltaMode ? 1 : 0.002) *
          (event.ctrlKey ? 10 : 1) *
          2,
      )
      .on('zoom', (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
        const s = sceneRef.current;
        if (!s.imageLayer || s.imgWidth == null || s.imgHeight == null) {
          return;
        }

        const t = event.transform;
        s.imageLayer.attr('transform', t.toString());

        s.markersLayers.forEach(({ factorX, factorY, element }) => {
          const x = factorX * s.imgWidth! * t.k + t.x;
          const y = factorY * s.imgHeight! * t.k + t.y;
          element.attr('transform', `translate(${x}, ${y})`);
        });

        if (s.mouseCircleGroup) {
          const mouseX = s.mouseFactorX * s.imgWidth * t.k + t.x;
          const mouseY = s.mouseFactorY * s.imgHeight * t.k + t.y;
          s.mouseCircleGroup.attr('transform', `translate(${mouseX}, ${mouseY})`);
        }

        zoomByImageRef.current.set(imageKeyRef.current, t);
        zoomTransformRef.current =
          s.existingGcpId != null && s.existingImageId != null
            ? { transform: t, gcpId: s.existingGcpId, imageId: s.existingImageId }
            : { transform: t };
      });

    d3.select(svgRef.current).call(zoomRef.current);
  }, []);

  useEffect(() => {
    if (!isDefined(signer) || !isDefined(image) || !isDefined(svgRef.current) || imageSrc == null) {
      return;
    }

    const imgElement = new Image();

    if (isDefined(image?.file)) {
      const reader = new FileReader();
      reader.onload = () => {
        imgElement.src = reader.result as string;
      };
      reader.readAsDataURL(image.file);
    } else {
      imgElement.src = imageSrc;
    }

    const existingMarker = isDefined(gcp)
      ? image?.markers.find((x) => x.gcpId === gcp.id)
      : undefined;

    const handleMarkerUpdate = (pinned: boolean, position: [number, number]) => {
      if (!isDefined(gcp?.id) || !isDefined(image.imageId) || !readyToOptimize) {
        return;
      }

      // It should not be possible to pin a marker if it has an active estimate, but it should be possible to unpin it.
      const disablePinning =
        pinned &&
        isDefined(existingMarker) &&
        estimates.some(
          (e) =>
            e.markerId === existingMarker.id && (e.status === 'PENDING' || e.status === 'ACCEPTED'),
        );

      if (disablePinning) {
        showToast({
          type: 'error',
          title: t('toaster.title.error', { ns: 'common' }),
          message: t('placeMarkersViewer.contextMenu.markerHasEstimateError', {
            ns: 'cloudProcessing',
          }),
        });
        return;
      }

      updateMarker({
        id: existingMarker?.id ?? v4(),
        gcpId: gcp.id,
        imageId: image.imageId,
        pinned: pinned,
        position: position,
        reprojectionError: getUpdatedReprojectionError(position, existingMarker),
      });
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === ' ' && isDefined(existingMarker)) {
        handleMarkerUpdate(!existingMarker.pinned, existingMarker.position);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    imgElement.onload = () => {
      const svgEl = svgRef.current!;
      const svg = d3.select<SVGSVGElement, unknown>(svgEl);

      // Capture current transform before rebuild so we can restore for same image
      const currentTransform = d3.zoomTransform(svgEl);
      zoomByImageRef.current.set(imageKey, currentTransform);

      // Sets the svg so it is always the same width to avoid issues with inconsistent scaling.
      const imgWidth = 2000;
      const imgHeight = imgElement.naturalHeight * (imgWidth / imgElement.naturalWidth);

      svg
        .attr('width', '100%')
        .attr('height', '100%')
        .style('cursor', 'none')
        .attr('viewBox', `0 0 ${imgWidth} ${imgHeight}`)
        .style('display', 'block');

      svg.selectAll('*').remove();

      const imageLayer = svg.append('g');
      const markersLayers: {
        factorX: number;
        factorY: number;
        element: d3.Selection<SVGGElement, unknown, null, undefined>;
      }[] = [];

      imageLayer
        .append('image')
        .attr('href', imgElement.src)
        .attr('width', imgWidth)
        .attr('height', imgHeight)
        .style('image-rendering', 'pixelated');

      const size = imgWidth * 0.015;
      const strokeWidth = imgWidth * 0.0012;

      for (const marker of image?.markers ?? []) {
        const matchingGcp = geodata.gcpCollection?.points.find((x) => x.id === marker.gcpId);

        // getMarkerEstimate returns non-null only for unpinned markers with an ACCEPTED or PENDING estimate.
        const estimate = getMarkerEstimate(marker, estimates);
        const hasActiveEstimate = !marker.pinned && estimate != null;

        // Show the original marker when pinned, or when unpinned with no active estimate.
        if (marker.pinned || !hasActiveEstimate) {
          const originalColor = marker.pinned ? pinnedGreen : theme.color.blue;
          markersLayers.push(
            getMarkerGroup(
              svg,
              marker.position,
              originalColor,
              size,
              strokeWidth,
              matchingGcp?.name,
            ),
          );
        }

        // Show the estimate in place of the unpinned marker when an ACCEPTED or PENDING estimate exists.
        if (hasActiveEstimate && estimate) {
          const isEstimateAccepted = estimate.status === 'ACCEPTED';
          const statusLabel = isEstimateAccepted
            ? t('aligner.accepted', { ns: 'cloudProcessing' })
            : t('aligner.suggestion', { ns: 'cloudProcessing' });
          const estimateLabel = matchingGcp?.name
            ? `${matchingGcp.name} (${statusLabel})`
            : undefined;
          markersLayers.push(
            getMarkerGroup(
              svg,
              estimate.position,
              isEstimateAccepted ? pinnedGreen : theme.color.brand.dark,
              size * 0.85,
              strokeWidth * 0.85,
              estimateLabel,
            ),
          );
        }

        // Display reprojection error relative to the original marker position
        if (
          isDefined(marker.reprojectionError) &&
          Math.abs(marker.reprojectionError[0] + marker.reprojectionError[1]) > 0
        ) {
          markersLayers.push(
            getMarkerGroup(
              svg,
              [
                marker.position[0] + marker.reprojectionError[0],
                marker.position[1] + marker.reprojectionError[1],
              ],
              'black',
              size * 0.75,
              strokeWidth,
              matchingGcp?.name,
            ),
          );
        }
      }

      const mouseCircleGroup = getMouseGroup(svg, size, strokeWidth, gcp?.name);

      sceneRef.current = {
        imageLayer,
        markersLayers,
        mouseCircleGroup,
        imgWidth,
        imgHeight,
        mouseFactorX: 0,
        mouseFactorY: 0,
        existingGcpId: existingMarker?.gcpId,
        existingImageId: existingMarker?.imageId,
      };

      const getMouseRelativePosition = (event: MouseEvent) => {
        const [x, y] = d3.pointer(event);
        // Get the current zoom transform to adjust for zoom and pan.
        const transform = d3.zoomTransform(imageLayer.node()!);

        // Calculate mouse position relative to the transformed image.
        const relativeX = (x - transform.x) / transform.k;
        const relativeY = (y - transform.y) / transform.k;

        // Calculate mouse factors based on the original image dimensions.
        return {
          x,
          y,
          relativeX: relativeX / imgWidth,
          relativeY: relativeY / imgHeight,
        };
      };

      // Update relative mouse position and show the circle.
      svg.on('mousemove', (event: MouseEvent) => {
        const mouse = getMouseRelativePosition(event);

        sceneRef.current.mouseFactorX = mouse.relativeX;
        sceneRef.current.mouseFactorY = mouse.relativeY;

        mouseCircleGroup
          .attr('transform', `translate(${mouse.x}, ${mouse.y})`)
          .style('display', 'block');
      });

      svg.on('mouseleave', () => {
        mouseCircleGroup.style('display', 'none');
      });

      // Reset per-scene click tracking so a pointerup orphaned by a mid-pan rebuild never
      // registers as a click (lastMouseDownRef stays 0 until a fresh pointerdown is seen).
      lastMouseDownRef.current = 0;
      lastMousePositionRef.current = undefined;

      svg.on('pointerdown', (event: MouseEvent) => {
        lastMouseDownRef.current = Date.now();
        lastMousePositionRef.current = { clientX: event.clientX, clientY: event.clientY };
      });

      svg.on('contextmenu', (event) => {
        if (!event.shiftKey) {
          event.preventDefault();
        }
      });

      svg.on('pointerup', (event: MouseEvent) => {
        // If no pointerdown was recorded in this scene build (e.g. rebuild happened mid-pan),
        // discard the event entirely to prevent a stray marker placement.
        if (lastMouseDownRef.current === 0) {
          return;
        }

        const elapsedTimeMs = Date.now() - lastMouseDownRef.current;
        const isClick = elapsedTimeMs < 250;
        const isLeftMouseButton = event.button === 0;
        const isRightMouseButton = event.button === 2;

        if (!isClick || !(isLeftMouseButton || isRightMouseButton)) {
          return;
        }

        const mouse = getMouseRelativePosition(event);

        // Use screen-pixel distance (clientX/Y) so the 10px threshold is meaningful
        // regardless of zoom level or viewBox scale.
        const movedDistance = isDefined(lastMousePositionRef.current)
          ? Math.hypot(
              event.clientX - lastMousePositionRef.current.clientX,
              event.clientY - lastMousePositionRef.current.clientY,
            )
          : 0;

        const mouseMovedTresholdPixels = 10;

        // If we move the mouse more than the threshold don't consider it a click
        if (movedDistance > mouseMovedTresholdPixels) {
          return;
        }

        if (isRightMouseButton) {
          const activeEstimate =
            existingMarker && !existingMarker.pinned
              ? estimates.find(
                  (e) =>
                    e.markerId === existingMarker.id &&
                    (e.status === 'PENDING' || e.status === 'ACCEPTED'),
                )
              : undefined;
          setMarkerContextMenu({
            x: event.clientX,
            y: event.clientY,
            marker: existingMarker,
            activeEstimate,
          });
          return;
        }

        handleMarkerUpdate(true, [mouse.relativeX, mouse.relativeY]);
      });

      // Only apply default (center-on-marker) when image changed; otherwise restore saved zoom
      const isNewImage = prevImageKeyRef.current !== imageKey;
      prevImageKeyRef.current = imageKey;
      imageKeyRef.current = imageKey;

      const saved = zoomByImageRef.current.get(imageKey);
      const initial = isNewImage
        ? getDefaultTransformation(
            imgWidth,
            imgHeight,
            existingMarker,
            zoomTransformRef.current,
            svgEl,
          )
        : saved ?? d3.zoomIdentity;

      if (isDefined(existingMarker)) {
        zoomTransformRef.current = {
          transform: initial,
          gcpId: existingMarker.gcpId,
          imageId: existingMarker.imageId,
        };
      } else {
        zoomTransformRef.current = { transform: initial };
      }

      if (zoomRef.current) {
        const zoom = zoomRef.current;
        if (isNewImage) {
          // New image: go through D3 to set both __zoom state and fire the zoom event.
          svg.call(zoom.transform.bind(zoom), initial);
        } else {
          // Same image rebuild (e.g. estimates/theme update): the SVG's __zoom is already
          // correct. Apply the transform directly to the new DOM elements so D3's internal
          // pan/drag gesture state is not interrupted mid-pan.
          const t = initial;
          sceneRef.current.imageLayer?.attr('transform', t.toString());
          sceneRef.current.markersLayers.forEach(({ factorX, factorY, element }) => {
            const x = factorX * imgWidth * t.k + t.x;
            const y = factorY * imgHeight * t.k + t.y;
            element.attr('transform', `translate(${x}, ${y})`);
          });
        }
      }
    };

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    signer,
    imageSrc,
    image,
    imageKey,
    gcp,
    geodata,
    updateMarker,
    readyToOptimize,
    estimates,
    showToast,
    theme,
  ]);

  if (!showPlaceMarkersDialog) {
    return null;
  }

  if (!isDefined(image)) {
    return <Center>No image selected</Center>;
  }

  return (
    <PlaceMarkerContainer>
      {markerContextMenu && (
        <ContextMenu
          left={markerContextMenu.x}
          top={markerContextMenu.y}
          onClose={() => setMarkerContextMenu(null)}
        >
          {(!isDefined(markerContextMenu.activeEstimate) || markerContextMenu.marker?.pinned) && (
            <MenuItem
              disabled={() => !isDefined(markerContextMenu.marker) || !readyToOptimize}
              hotkey={t('placeMarkersViewer.contextMenu.pinMarkerHotkey', {
                ns: 'cloudProcessing',
              })}
              label={
                markerContextMenu.marker?.pinned
                  ? t('placeMarkersViewer.contextMenu.unpinMarker', { ns: 'cloudProcessing' })
                  : t('placeMarkersViewer.contextMenu.pinMarker', { ns: 'cloudProcessing' })
              }
              onClick={() => {
                if (isDefined(markerContextMenu.marker)) {
                  updateMarker({
                    ...markerContextMenu.marker,
                    pinned: !markerContextMenu.marker.pinned,
                  });
                }
              }}
            />
          )}
          {isDefined(markerContextMenu.activeEstimate) && (
            <>
              {markerContextMenu.activeEstimate.status === 'PENDING' && (
                <MenuItem
                  disabled={() => isPatchingEstimate || !readyToOptimize}
                  label={t('placeMarkersViewer.contextMenu.acceptEstimate', {
                    ns: 'cloudProcessing',
                  })}
                  onClick={async () => {
                    await patchMarkerEstimatesAsync({
                      geodataId: geodata.id,
                      body: { ids: [markerContextMenu.activeEstimate!.id], status: 'ACCEPTED' },
                    });
                  }}
                />
              )}
              <MenuItem
                disabled={() => isPatchingEstimate || !readyToOptimize}
                label={t('placeMarkersViewer.contextMenu.rejectEstimate', {
                  ns: 'cloudProcessing',
                })}
                onClick={async () => {
                  await patchMarkerEstimatesAsync({
                    geodataId: geodata.id,
                    body: { ids: [markerContextMenu.activeEstimate!.id], status: 'REJECTED' },
                  });
                }}
              />
            </>
          )}
          <MenuItem
            disabled={() => !isDefined(markerContextMenu.marker) || !readyToOptimize}
            label={t('placeMarkersViewer.contextMenu.removeMarker', { ns: 'cloudProcessing' })}
            onClick={async () => {
              if (isDefined(markerContextMenu.marker)) {
                await deleteMarker(markerContextMenu.marker.id);
              }
            }}
          />
        </ContextMenu>
      )}
      <Content>
        <svg ref={svgRef} />

        <Toolbar spacing={0.2}>
          <BottomCenter>
            <ToolbarGroup>
              <Button
                color="secondary"
                colorText="black"
                leftIcon={{ icon: ['fal', 'xmark'] }}
                padding={'10px'}
                ref={closeButtonRef}
                variant="text"
                onClick={() => setShowPlaceMarkersDialog(false)}
              >
                {t('close', { ns: 'common' })}
              </Button>
            </ToolbarGroup>
            <ToolbarGroup>
              <ToolIcon
                active={false}
                callback={async () => {
                  try {
                    setPatchImageLoading(true);
                    const result = await SkyMapAxiosServiceFactory.instance
                      .createGeodataServiceV0()
                      .patchGeodataImage({
                        path: {
                          geodataId: geodata.id,
                          imageId: image.imageId!,
                        },
                        body: {
                          enabled: !image.enabled,
                        },
                      });
                    image.enabled = result.data.enabled;
                  } finally {
                    setPatchImageLoading(false);
                  }
                }}
                disabled={patchImageLoading}
                fixedWidth={true}
                icon={image.enabled ? ['fal', 'image'] : ['fal', 'image-slash']}
                tooltip={{
                  title: image.enabled
                    ? t('aligner.images.enabledTitle', { ns: 'cloudProcessing' })
                    : t('aligner.images.disabledTitle', { ns: 'cloudProcessing' }),
                  text: image.enabled
                    ? t('aligner.images.enabledInfo', { ns: 'cloudProcessing' })
                    : t('aligner.images.disabledInfo', { ns: 'cloudProcessing' }),
                }}
              />
              <ToolIcon
                active={false}
                callback={async () => {
                  try {
                    setPatchImageLoading(true);
                    const result = await SkyMapAxiosServiceFactory.instance
                      .createGeodataServiceV0()
                      .patchGeodataImage({
                        path: {
                          geodataId: geodata.id,
                          imageId: image.imageId!,
                        },
                        body: {
                          useGpsPosition: !image.useGps,
                        },
                      });
                    image.useGps = result.data.useGps;
                  } finally {
                    setPatchImageLoading(false);
                  }
                }}
                disabled={patchImageLoading}
                fixedWidth={true}
                icon={image.useGps ? ['fal', 'location-dot'] : ['fal', 'location-dot-slash']}
                tooltip={{
                  title: image.useGps
                    ? t('aligner.images.gpsEnabledTitle', { ns: 'cloudProcessing' })
                    : t('aligner.images.gpsDisabledTitle', { ns: 'cloudProcessing' }),
                  text: image.useGps
                    ? t('aligner.images.gpsEnabledInfo', { ns: 'cloudProcessing' })
                    : t('aligner.images.gpsDisabledInfo', { ns: 'cloudProcessing' }),
                }}
              />
              <ToolIcon
                active={fullResolution}
                callback={() => {
                  const newValue = !fullResolution;
                  setFullResolution(newValue);
                  sessionStorageUtils.setUserItem(
                    'placeMarkersFullResolution',
                    newValue ? 'true' : 'false',
                  );
                }}
                disabled={false}
                fixedWidth={true}
                icon={fullResolution ? ['fal', 'high-definition'] : ['fal', 'standard-definition']}
                tooltip={{
                  title: fullResolution
                    ? t('aligner.images.fullResolutionEnabledTitle', { ns: 'cloudProcessing' })
                    : t('aligner.images.fullResolutionDisabledTitle', { ns: 'cloudProcessing' }),
                  text: t('aligner.images.fullResolutionInfo', { ns: 'cloudProcessing' }),
                }}
              />
              <ToolIcon
                active={false}
                callback={handleDownloadImage}
                disabled={downloadLoading || !isDefined(image?.imageIdentifier)}
                fixedWidth={true}
                icon={['fal', 'cloud-download']}
                tooltip={{
                  title: t('aligner.images.downloadOriginalTitle', { ns: 'cloudProcessing' }),
                  text: t('aligner.images.downloadOriginalInfo', { ns: 'cloudProcessing' }),
                }}
              />
            </ToolbarGroup>
            <ToolbarGroup>
              <Button
                color="secondary"
                colorText="black"
                leftIcon={{ icon: ['fal', 'arrow-up'] }}
                padding={'10px'}
                ref={previousButtonRef}
                variant="text"
                onClick={() =>
                  selectNextOrPreviousImage(
                    imagesWithMarkers,
                    sortedImageList,
                    geodata,
                    selectImage,
                    selectGcp,
                    image,
                    gcp,
                    'previous',
                  )
                }
              >
                {t('previous', { ns: 'common' })}
              </Button>

              <Button
                color="secondary"
                colorText="black"
                leftIcon={{ icon: ['fal', 'arrow-down'] }}
                padding={'10px'}
                ref={nextButtonRef}
                variant="text"
                onClick={() =>
                  selectNextOrPreviousImage(
                    imagesWithMarkers,
                    sortedImageList,
                    geodata,
                    selectImage,
                    selectGcp,
                    image,
                    gcp,
                    'next',
                  )
                }
              >
                {t('next', { ns: 'common' })}
              </Button>
            </ToolbarGroup>
          </BottomCenter>
        </Toolbar>

        <TopCornerContainer>
          <Label>IMG: {image?.displayName ?? 'N/A'}</Label>
          <Label>GCP: {gcp?.name ?? 'N/A'}</Label>
        </TopCornerContainer>
      </Content>
    </PlaceMarkerContainer>
  );
};

const Toolbar = styled(Stack)`
  align-items: center;
  justify-content: center;
`;

const BottomCenter = styled.div`
  position: absolute;
  bottom: 16px;

  display: flex;
  flex-wrap: wrap;
  border-radius: 5px;

  margin: 0 16px;
  padding: 8px;

  background-color: #fff;
  box-shadow:
    0 3px 6px rgba(0, 0, 0, 0.16),
    0 3px 6px rgba(0, 0, 0, 0.23);

  z-index: ${sfmViewerLayering.placeMarkersContainerMenu};
`;

const TopCornerContainer = styled(Stack)`
  position: absolute;
  top: 0.5em;
  left: 0.5em;
  z-index: ${sfmViewerLayering.placeMarkersContainerMenu};
`;

const Label = styled.label`
  color: white;
  font-weight: bold;
  font-size: 1.5em;
`;

const PlaceMarkerContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: ${sfmViewerLayering.placeMarkersContainer};
`;

const Content = styled.div`
  background-color: black;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;
